import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const GivaLogo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
    <img src={LOGO_URL} alt="Giva Solutions" className="h-10 w-10 rounded-xl bg-black object-contain sm:h-11 sm:w-11" />
    <span className="font-display text-lg font-bold tracking-tight text-giva-navy sm:text-xl">
      Giva<span className="text-giva-accent">.</span>
    </span>
  </Link>
);

function LangSwitcher({ light = false }) {
  const { lang, setLang } = useI18n();
  return (
    <div
      data-testid="lang-switcher"
      className={`flex shrink-0 items-center gap-1 rounded-full border p-1 text-xs font-semibold ${
        light ? "border-white/25 text-white" : "border-giva-line text-giva-text2"
      }`}
    >
      <Globe className="ml-1.5 h-3.5 w-3.5 opacity-70" />
      {["en", "pt"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`rounded-full px-2 py-1 uppercase transition-colors sm:px-2.5 ${
            lang === l ? "bg-giva-primary text-white" : "hover:text-giva-primary"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function SiteHeader() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const links = [
    { label: t("nav.services"), href: "/#servicos", id: "services" },
    { label: t("nav.how"), href: "/#como-funciona", id: "how" },
    { label: t("nav.blog"), href: "/blog", id: "blog" },
    { label: t("nav.contact"), href: "/#contacto", id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-white/40 bg-white/70 backdrop-blur-xl shadow-sm" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <GivaLogo />

        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-${l.id}`}
              className="text-sm font-medium text-giva-text2 transition-colors hover:text-giva-primary"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 md:flex">
          <LangSwitcher />
          <a
            href="/#download"
            data-testid="header-download-btn"
            className="rounded-full bg-giva-primary px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] hover:bg-giva-primaryHover"
          >
            {t("nav.download")}
          </a>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LangSwitcher />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="rounded-lg p-2 text-giva-navy shrink-0"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-giva-line bg-white px-5 py-4 md:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <a key={l.id} href={l.href} className="rounded-lg px-3 py-3 text-base font-medium text-giva-text hover:bg-giva-surface2">
                {l.label}
              </a>
            ))}
            <a href="/#download" className="mt-2 rounded-full bg-giva-primary px-5 py-3 text-center text-sm font-semibold text-white">
              {t("nav.download")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
